import React from "react"

import {
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	List,
	ListItemButton,
	Pagination,
	Stack,
	Typography,
} from "@mui/material"

import Icon from "@mdi/react"
import {
	mdiAccountOutline,
	mdiAlert,
	mdiBell,
	mdiCheckCircleOutline,
	mdiCog,
	mdiHomeCity,
	mdiHomeSwitch,
	mdiStore,
} from "@mdi/js"

import {
	t,
	plural,
} from "@lingui/macro"

import {
	DateTime,
} from "luxon"
import {
	convertFromRaw,
} from "draft-js"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import {
	Action,
	ActionStatus,
	ActionsRequest,
	PaginatedResponse,
	SessionResponse,
} from "models"
import { ActionsServices } from "services/actions.services"
import { PageHeader, PageSectionTitle, RoutesData } from "components/commons"
import { SbSnackbar } from "components/commons/sb-snackbar"
import { MatesComponent, MatesComponentState } from "components/mates-component"
import withUseRouter, { UseRouterParams } from "services/router"

interface NotificationsProps extends UseRouterParams {
	sessionData: SessionResponse
}

interface NotificationsState extends MatesComponentState {
	loading: boolean
	request: ActionsRequest
	notifications: PaginatedResponse<Action> | null
	selectedNotificationId: string | null
	hiddenNotificationIds: Set<string>
	snackbarData: {
		openNotification: boolean
		severity: "error" | "info" | "success" | "warning"
		notificationMessage: string
	}
}

class Notifications extends React.Component<NotificationsProps, NotificationsState> {
	private readonly matesComponent: MatesComponent<NotificationsProps, NotificationsState>

	constructor(props: NotificationsProps) {
		super(props)
		this.matesComponent = new MatesComponent(this)
		this.state = {
			...this.matesComponent.setupState(),
			loading: true,
			request: ActionsRequest.empty(),
			notifications: null,
			selectedNotificationId: null,
			hiddenNotificationIds: new Set(),
			snackbarData: {
				openNotification: false,
				severity: "info",
				notificationMessage: "",
			},
		}
	}

	componentDidMount() {
		this.loadNotifications()
	}

	private loadNotifications = () => {
		this.setState(
			() => ({ loading: true }),
			() => ActionsServices.getNotificationActions(
				this.state.request,
				this.getNotificationsCallback,
				this.matesComponent.onException,
			),
		)
	}

	private getNotificationsCallback = (response: RestResponse<PaginatedResponse<Action>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		const items = response.data?.items ?? []
		const selectedNotificationId = this.resolveSelectedNotificationId(items, this.state.selectedNotificationId)

		this.setState(() => ({
			loading: false,
			notifications: response.data,
			selectedNotificationId,
		}))
	}

	private resolveSelectedNotificationId = (items: Action[], previousSelection: string | null): string | null => {
		if (items.length === 0) {
			return null
		}

		if (previousSelection && items.some((notification) => notification.id === previousSelection)) {
			return previousSelection
		}

		const firstVisible = items.find((notification) => !this.state.hiddenNotificationIds.has(notification.id))
		return firstVisible ? firstVisible.id : items[0].id
	}

	private getVisibleNotifications = (): Action[] => {
		const items = this.state.notifications?.items ?? []
		const filtered = this.state.hiddenNotificationIds.size === 0
			? items
			: items.filter((notification) => !this.state.hiddenNotificationIds.has(notification.id))

		return [...filtered].sort(
			(a, b) => this.getNotificationTimestamp(b) - this.getNotificationTimestamp(a)
		)
	}

	private getSelectedNotification = (visibleNotifications: Action[]): Action | null => {
		if (visibleNotifications.length === 0) {
			return null
		}

		if (!this.state.selectedNotificationId) {
			return visibleNotifications[0]
		}

		return visibleNotifications.find((notification) => notification.id === this.state.selectedNotificationId) ?? visibleNotifications[0]
	}

	private isNotificationRead = (notification: Action): boolean => {
		const notificationData = notification as Action & {
			isRead?: boolean
			read?: boolean
			unread?: boolean
			readAt?: string | null
			readDate?: string | null
		}

		if (typeof notificationData.isRead === "boolean") {
			return notificationData.isRead
		}
		if (typeof notificationData.read === "boolean") {
			return notificationData.read
		}
		if (typeof notificationData.unread === "boolean") {
			return !notificationData.unread
		}
		if (notificationData.readAt || notificationData.readDate) {
			return true
		}

		return notification.status === ActionStatus.DONE || notification.status === ActionStatus.SKIPPED
	}

	private getNotificationType = (notification: Action): string => {
		const rawType = (notification as any)?.type ?? (notification as any)?.notificationType ?? null
		if (typeof rawType !== "string") {
			return "GENERIC"
		}

		const normalized = rawType.toUpperCase()
		const allowedTypes = ["SYSTEM", "PROPOSAL", "MATCHING", "ALERT", "ASSET", "GENERIC"]
		return allowedTypes.includes(normalized) ? normalized : "GENERIC"
	}

	private getNotificationIcon = (notification: Action): string => {
		switch (this.getNotificationType(notification)) {
			case "SYSTEM":
				return mdiCog
			case "PROPOSAL":
				return mdiStore
			case "MATCHING":
				return mdiHomeSwitch
			case "ALERT":
				return mdiAlert
			case "ASSET":
				return mdiHomeCity
			case "GENERIC":
			default:
				return mdiBell
		}
	}

	private getNotificationTypeLabel = (notification: Action): string => {
		switch (this.getNotificationType(notification)) {
			case "SYSTEM":
				return t({ id: "notifications.type.system", message: "Sistema" })
			case "PROPOSAL":
				return t({ id: "notifications.type.proposal", message: "Proposta" })
			case "MATCHING":
				return t({ id: "notifications.type.matching", message: "Matching" })
			case "ALERT":
				return t({ id: "notifications.type.alert", message: "Avviso" })
			case "ASSET":
				return t({ id: "notifications.type.asset", message: "Immobile" })
			case "GENERIC":
			default:
				return t({ id: "notifications.type.generic", message: "Generica" })
		}
	}

	private getNotificationTimestamp = (notification: Action): number => {
		const dateCandidates = [
			(notification as any)?.readDate,
			(notification as any)?.readAt,
			notification.executionDate,
			notification.creationDate,
			notification.deadline,
		]

		for (const candidate of dateCandidates) {
			if (!candidate) {
				continue
			}
			const parsed = new Date(candidate)
			const timestamp = parsed.getTime()
			if (!Number.isNaN(timestamp)) {
				return timestamp
			}
		}

		return 0
	}

	private formatNotificationDate = (notification: Action): string => {
		const timestamp = this.getNotificationTimestamp(notification)
		if (!timestamp) {
			return "-"
		}

		return DateTime.fromMillis(timestamp).toFormat("dd/LL/yyyy HH:mm")
	}

	private extractPlainText = (notification: Action | null): string => {
		if (!notification) {
			return ""
		}

		try {
			if (typeof notification.content === "string") {
				return notification.content
			}

			return convertFromRaw(notification.content).getPlainText("\n")
		} catch {
			return ""
		}
	}

	private openNotificationDetails = (notification: Action) => {
		this.setState(
			() => ({ selectedNotificationId: notification.id }),
			() => {
				if (!this.isNotificationRead(notification)) {
					this.markNotificationAsRead(notification.id)
				}
			},
		)
	}

	private markNotificationAsRead = (notificationId: string) => {
		ActionsServices.markNotificationAsRead(
			notificationId,
			() => {
				this.handleOpenNotification(
					t({ id: "notifications.mark-as-read.success", message: "Notifica segnata come letta." }),
					"success",
				)
				this.loadNotifications()
			},
			() => {
				this.handleOpenNotification(
					t({ id: "notifications.mark-as-read.error", message: "Errore nel marcare la notifica come letta." }),
					"error",
				)
			},
		)
	}

	private hideNotification = (notificationId: string) => {
		this.setState(
			(prev) => {
				const hiddenNotificationIds = new Set(prev.hiddenNotificationIds)
				hiddenNotificationIds.add(notificationId)
				return { hiddenNotificationIds }
			},
			this.ensureSelectionAfterVisibilityChange,
		)
	}

	private ensureSelectionAfterVisibilityChange = () => {
		this.setState((prev) => {
			const visible = (prev.notifications?.items ?? [])
				.filter((notification) => !prev.hiddenNotificationIds.has(notification.id))

			if (visible.length === 0) {
				if (prev.selectedNotificationId === null) {
					return null
				}
				return { selectedNotificationId: null }
			}

			if (prev.selectedNotificationId && visible.some((notification) => notification.id === prev.selectedNotificationId)) {
				return null
			}

			return { selectedNotificationId: visible[0].id }
		})
	}

	private handlePaginationChange = (_: React.ChangeEvent<unknown>, page: number) => {
		this.setState(
			(prev) => ({
				request: {
					...prev.request,
					pageId: page - 1,
				},
			}),
			this.loadNotifications,
		)
	}

	private handleOpenNotification = (
		message: string,
		severity: "error" | "info" | "success" | "warning",
	) => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity,
			},
		}))
	}

	private handleCloseNotification = () => {
		this.setState((prev) => ({
			snackbarData: {
				...prev.snackbarData,
				openNotification: false,
			},
		}))
	}

	private getDetailLink = (notification: Action | null): string | null => {
		if (!notification) {
			return null
		}

		const metadata = (notification as any)?.metadata ?? {}
		if (metadata.assetId) {
			return `/asset/${metadata.assetId}`
		}
		if (metadata.matchId) {
			return `/match/${metadata.matchId}`
		}

		return null
	}

	render() {
		const visibleNotifications = this.getVisibleNotifications()
		const selectedNotification = this.getSelectedNotification(visibleNotifications)
		const unreadCount = visibleNotifications.filter((notification) => !this.isNotificationRead(notification)).length
		const pagination = this.state.notifications?.pagination
		const detailLink = this.getDetailLink(selectedNotification)

		return (
			<Box sx={{ pt: 2 }}>
				<Loading show={this.state.loading} />
				<PageHeader
					title={RoutesData.NOTIFICATIONS.title}
					paths={[RoutesData.DASHBOARD, RoutesData.NOTIFICATIONS]}
				/>

				<Box sx={{ mb: 3 }}>
					<PageSectionTitle
						title={plural(unreadCount, {
							one: "# Notifica da leggere",
							other: "# Notifiche da leggere",
						})}
						color="primary.main"
					/>
				</Box>

				<Box sx={{ bgcolor: "background.default", p: 3, borderRadius: 2 }}>
					<Grid container alignItems="start" columnSpacing={3} maxHeight="70vh">
						<Grid item sm={12} md={7}>
							{visibleNotifications.length === 0 && (
								<Typography pt={5} textAlign="center" height="60vh">
									{t({ id: "notifications.empty", message: "Non ci sono notifiche da mostrare." })}
								</Typography>
							)}

							{visibleNotifications.length > 0 && (
								<Stack direction="column" alignItems="center" height="60vh">
									<Pagination
										showFirstButton
										showLastButton
										color="primary"
										page={(pagination?.currentPage ?? 0) + 1}
										count={pagination?.totalPages ?? 1}
										onChange={this.handlePaginationChange}
									/>
									<List sx={{ width: "100%", overflow: "auto" }}>
										{visibleNotifications.map((notification) => {
											const isSelected = this.state.selectedNotificationId === notification.id
											const isRead = this.isNotificationRead(notification)

											return (
												<Box key={notification.id}>
													<ListItemButton
														selected={isSelected}
														onClick={() => this.openNotificationDetails(notification)}
														sx={{
															alignItems: "flex-start",
															bgcolor: isRead ? "background.default" : "#E7F0FF",
														}}
													>
														<Box display="flex" width="100%" columnGap={2}>
															<Box
																component={Icon}
																path={this.getNotificationIcon(notification)}
																size="32px"
																sx={{
																	color: "primary.contrastText",
																	backgroundColor: isRead ? "primary.main" : "#11347B",
																	padding: "5px",
																	borderRadius: "50%",
																	mt: 1,
																}}
															/>
															<Box flex={1}>
																<Typography sx={{ fontSize: 16, fontWeight: "600", color: "primary.main" }}>
																	{notification.name}
																</Typography>
																<Typography sx={{ fontSize: 11 }}>
																	{t({
																		id: "notifications.type-label",
																		message: "Tipo di notifica:",
																	})}{" "}
																	{this.getNotificationTypeLabel(notification)}
																</Typography>
																<Typography sx={{ fontSize: 11 }}>
																	{t({
																		id: "notifications.date-label",
																		message: "Data:",
																	})}{" "}
																	{this.formatNotificationDate(notification)}
																</Typography>
															</Box>
															<Stack direction="row" spacing={1} alignItems="flex-start">
																<IconButton
																	size="small"
																	onClick={(event) => {
																		event.stopPropagation()
																		if (!isRead) {
																			this.markNotificationAsRead(notification.id)
																		}
																	}}
																	disabled={isRead}
																	sx={{ color: isRead ? "success.light" : "success.main" }}
																>
																	<Icon path={mdiCheckCircleOutline} size={0.9} />
																</IconButton>
															</Stack>
														</Box>
													</ListItemButton>
													<Divider />
												</Box>
											)
										})}
									</List>
								</Stack>
							)}
						</Grid>

						<Grid item sm={12} md={5}>
							<Box
								sx={{
									p: 3,
									bgcolor: "background.paper",
									borderRadius: 2,
									minHeight: "60vh",
									overflow: "auto",
								}}
							>
								<Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
									{t({ id: "notifications.details.title", message: "Dettagli" })}
								</Typography>
								<Divider sx={{ my: 2 }} />

								{selectedNotification ? (
									<Stack spacing={2}>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{t({ id: "notifications.details.name", message: "Nome" })}
											</Typography>
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{selectedNotification.name}
											</Typography>
										</Box>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{t({ id: "notifications.details.type", message: "Tipo di notifica" })}
											</Typography>
											<Typography variant="body1">
												{this.getNotificationTypeLabel(selectedNotification)}
											</Typography>
										</Box>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{t({ id: "notifications.details.message", message: "Messaggio" })}
											</Typography>
											<Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
												{this.extractPlainText(selectedNotification) || "-"}
											</Typography>
										</Box>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{t({ id: "notifications.details.date", message: "Data" })}
											</Typography>
											<Typography variant="body1">{this.formatNotificationDate(selectedNotification)}</Typography>
										</Box>
										{detailLink && (
											<Button
												variant="contained"
												fullWidth
												onClick={() => this.props.navigate(detailLink)}
												startIcon={<Icon path={mdiAccountOutline} size={1} />}
												sx={{ textTransform: "none", bgcolor: "#1f6bff" }}
											>
												{t({ id: "notifications.details.open", message: "Vai alla scheda" })}
											</Button>
										)}
									</Stack>
								) : (
									<Box py={6} textAlign="center">
										<Typography variant="body2" color="text.secondary">
											{t({ id: "notifications.details.empty", message: "Seleziona una notifica per vedere i dettagli." })}
										</Typography>
									</Box>
								)}
							</Box>
						</Grid>
					</Grid>
				</Box>

				<SbSnackbar
					snackbarData={this.state.snackbarData}
					handleCloseNotification={this.handleCloseNotification}
				/>
			</Box>
		)
	}
}

export default withUseRouter(Notifications)
